// src/pages/competition/CompetitionContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePolling } from '../../context/PollingContext';

const CompetitionContext = createContext();

export const useCompetition = () => useContext(CompetitionContext);

export const CompetitionProvider = ({ children }) => {
  const { connected, startSession, leaveSession } = usePolling();
  const [competitionId, setCompetitionId] = useState(null);
  const [competitionCode, setCompetitionCode] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [role, setRole] = useState(null); // 'tabulator' or 'judge'
  const [judgeId, setJudgeId] = useState(null);
  const [judgeProfile, setJudgeProfile] = useState(null); // Add judge profile state
  const [judgePosition, setJudgePosition] = useState(null); // Add judge position state
  const [isConnected, setIsConnected] = useState(false);
  const [lastJoinedTimestamp, setLastJoinedTimestamp] = useState(null);

  // Note: Removed localStorage initialization - now using URL-based loading from AWS

  // Join competition room when connected or when competitionCode changes
  useEffect(() => {
    if (connected && competitionCode && role) {
      console.log(`CompetitionContext: Rejoining competition ${competitionCode} as ${role}${judgeId ? ` (Judge ${judgeId})` : ''}`);
      
      // For judges, we need to actually call startSession to register with the backend
      if (role === 'judge' && judgeId && judgeProfile && judgePosition) {
        console.log('CompetitionContext: Calling startSession for judge');
        startSession(competitionCode, role, [], judgeId, judgeProfile, judgePosition)
          .then(() => {
            console.log('CompetitionContext: Judge session started successfully');
            setIsConnected(true);
            setLastJoinedTimestamp(new Date().toISOString());
          })
          .catch((error) => {
            console.error('CompetitionContext: Failed to start judge session:', error);
            setIsConnected(false);
          });
      } else {
        // For tabulator or when no judge info, just mark as connected
        setIsConnected(true);
        setLastJoinedTimestamp(new Date().toISOString());
      }
    } else {
      setIsConnected(false);
    }
  }, [connected, competitionCode, role, judgeId, judgeProfile, judgePosition, startSession]);

  // Polling system handles connection status automatically
  // No need for event listeners

  // Save competition data (now just updates state - AWS is the source of truth)
  const saveCompetitionData = (data) => {
    setCompetitionId(data.competitionId || competitionId);
    setCompetitionCode(data.competitionCode || competitionCode);
    setDisplayName(data.displayName || displayName);
    setRole(data.role || role);
    setJudgeId(data.judgeId || judgeId);
    setJudgeProfile(data.judgeProfile || judgeProfile);
    setJudgePosition(data.judgePosition || judgePosition);
    setLastJoinedTimestamp(new Date().toISOString());
    
    // Note: No longer saving to localStorage - AWS is the single source of truth
  };

  // Clear competition data
  const clearCompetitionData = () => {
    // Leave the current session if connected
    if (connected && competitionCode) {
      if (typeof leaveSession === 'function') {
        leaveSession();
      }
    }
    
    setCompetitionId(null);
    setCompetitionCode(null);
    setDisplayName(null);
    setRole(null);
    setJudgeId(null);
    setJudgeProfile(null);
    setJudgePosition(null);
    setLastJoinedTimestamp(null);
    setIsConnected(false);
    
    // Note: No longer clearing localStorage - AWS is the single source of truth
  };

  return (
    <CompetitionContext.Provider
      value={{
        competitionId,
        competitionCode,
        displayName,
        role,
        judgeId,
        judgeProfile,
        isConnected,
        lastJoinedTimestamp,
        saveCompetitionData,
        clearCompetitionData
      }}
    >
      {children}
    </CompetitionContext.Provider>
  );
};